import React, { FC, useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { FormProvider, useForm, Controller } from "react-hook-form";
import FormInput from "../../../form-components/InputComponent";
import FormDatePicker from "../../../form-components/DatePicker";
import FormSelect from "../../../form-components/SelectComponent";
import dayjs from "dayjs";
import { Icon } from "../../../../components/Component";
import Swal from "sweetalert2";
import sitebreakdownMasterApis from "../../../../api/master/sitebreakdownMaster";

interface SubUnit {
  id: string;
  name: string;
  size: string;
  floor: number;
  startDate: string;
  endDate: string;
  description?: string;
  floorType?: string;
}

interface AddSiteStructureFormProps {
  projectType: string;
  onSave: (data: any) => void;
  onCancel: () => void;
  editData?: any;
  error?: { type: string; message: string } | null;
  onClearError?: () => void;
}

const AddSiteStructureForm: FC<AddSiteStructureFormProps> = ({
  projectType,
  onSave,
  onCancel,
  editData,
  error,
  onClearError,
}) => {
  const methods = useForm({
    defaultValues: {
      unitName: "",
      unitQty: "",
      noOfFloors: "",
      unitPerFloor: "",
      plannedStartDate: null,
      plannedEndDate: null,
      captureSubUnits: "No",
      description: "",
    },
  });

  const { handleSubmit, control, watch, reset, getValues, setValue } = methods;
  const [subUnits, setSubUnits] = useState<SubUnit[]>([]);
  const [alertInfo, setAlertInfo] = useState<{ type: string; message: string }>(
    {
      type: "",
      message: "",
    }
  );
  const captureSubUnits = watch("captureSubUnits");
  const noOfFloors = Number(watch("noOfFloors") || 0);
  const unitPerFloor = Number(watch("unitPerFloor") || 0);

  // new state for modal
  const [prevFloors, setPrevFloors] = useState<number>(noOfFloors);
  const [prevUnitsPerFloor, setPrevUnitsPerFloor] =
    useState<number>(unitPerFloor);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    floors: number;
    units: number;
  } | null>(null);

  // config helper
  const getConfig = () => {
    switch (projectType) {
      case "1": // Villa
        return {
          unitLabel: "Villa",
          unitPlural: "Villas",
          hasFloors: false,
          hasUnitPerFloor: false,
          hasSubUnits: false,
          sizeLabel: "SFT",
          subUnitType: "",
          hasFloorType: false,
        };
      case "2": // Highrise
      case "3": // Standalone
        return {
          unitLabel: projectType === "2" ? "Tower" : "Block",
          unitPlural: projectType === "2" ? "Towers" : "Blocks",
          hasFloors: true,
          hasUnitPerFloor: true,
          hasSubUnits: true,
          sizeLabel: "SFT",
          subUnitType: "Flat",
          subUnitName: "Flat Number",
          subUnitSizeLabel: "SFT",
          hasFloorType: true,
          floorTypeOptions: [
            { label: "Residential", value: "residential" },
            { label: "Parking", value: "parking" },
          ],
        };
      case "4": // Commercial
        return {
          unitLabel: "Tower",
          unitPlural: "Towers",
          hasFloors: true,
          hasUnitPerFloor: true,
          hasSubUnits: true,
          sizeLabel: "SFT",
          subUnitType: "Shop",
          subUnitName: "Shop Number",
          subUnitSizeLabel: "SFT",
          hasDescription: true,
          hasFloorType: true,
          floorTypeOptions: [
            { label: "Commercial", value: "commercial" },
            { label: "Parking", value: "parking" },
          ],
        };
      case "5": // Infra
        return {
          unitLabel: "Phase",
          unitPlural: "Phases",
          hasFloors: false,
          hasUnitPerFloor: false,
          hasSubUnits: true,
          sizeLabel: "Cubic Meters",
          subUnitType: "Milestone",
          subUnitName: "Milestone Name",
          subUnitSizeLabel: "Cubic Meters",
          hasDescription: true,
          hasFloorType: false,
        };
      default:
        return {
          unitLabel: "Unit",
          unitPlural: "Units",
          hasFloors: false,
          hasUnitPerFloor: false,
          hasSubUnits: false,
          sizeLabel: "Units",
          subUnitType: "",
          hasFloorType: false,
        };
    }
  };

  const config = getConfig();

  useEffect(() => {
    if (!config.hasSubUnits || subUnits.length === 0) {
      setPrevFloors(noOfFloors);
      setPrevUnitsPerFloor(unitPerFloor);
      return;
    }
  }, [noOfFloors, unitPerFloor, config.hasSubUnits, subUnits.length]);

  useEffect(() => {
    if (!config.hasSubUnits || subUnits.length === 0) {
      return;
    }

    const floorChanged = noOfFloors !== prevFloors;
    const unitChanged = unitPerFloor !== prevUnitsPerFloor;

    if ((floorChanged || unitChanged) && subUnits.length > 0) {
      const newFloors = noOfFloors;
      const newUnits = unitPerFloor;
      const currentValues = getValues();
      reset({
        ...currentValues,
        noOfFloors: prevFloors ? prevFloors.toString() : "",
        unitPerFloor: prevUnitsPerFloor ? prevUnitsPerFloor.toString() : "",
      });

      Swal.fire({
        title: "Are you sure?",
        text: `Changing Floors or Units per Floor will discard existing ${config.subUnitType} details.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Discard",
        cancelButtonText: "No, Cancel",
        allowOutsideClick: false,
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ms-2",
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const ids =
              editData?.subUnits
                ?.filter(
                  (s: any) => s.id && !s.id.toString().startsWith("subunit-")
                )
                ?.map((s: any) => Number(s.id)) || [];

            // console.log(ids, "ids");

            if (ids.length > 0 && editData?.id && subUnits.length > 0) {
              const payload = { ids };
              try {
                await sitebreakdownMasterApis.deleteSubUnitsByIds(payload);
                if (editData?.subUnits) {
                  editData.subUnits = editData.subUnits.filter(
                    (s: any) => !ids.includes(Number(s.id))
                  );
                }
              } catch (deleteError: any) {
                const msg =
                  deleteError?.response?.data?.message ||
                  deleteError?.message ||
                  "Failed to delete sub-units. Please try again.";

                Swal.fire({
                  icon: "error",
                  title: "Delete Failed",
                  text: msg,
                  confirmButtonText: "OK",
                  buttonsStyling: false,
                  customClass: { confirmButton: "btn btn-primary" },
                });
              }
            }
            setSubUnits([]);
            reset((formValues) => ({
              ...formValues,
              noOfFloors: "",
              unitPerFloor: "",
            }));
            setPrevFloors(0);
            setPrevUnitsPerFloor(0);
          } catch (error: any) {
            const msg =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to delete sub-units. Please try again.";

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: msg,
              confirmButtonText: "OK",
              buttonsStyling: false,
              customClass: { confirmButton: "btn btn-primary" },
            });
          }
        } else {
          reset((formValues) => ({
            ...formValues,
            noOfFloors: prevFloors.toString(),
            unitPerFloor: prevUnitsPerFloor.toString(),
          }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noOfFloors, unitPerFloor]);

  useEffect(() => {
    if (!config.hasSubUnits || !config.hasFloors) return;
    setSubUnits((prevSubUnits) => {
      if (!noOfFloors || !unitPerFloor) return prevSubUnits;
      if (editData && prevSubUnits.length > 0) {
        return prevSubUnits;
      }

      const newSubUnits: SubUnit[] = [];

      for (let floor = 1; floor <= noOfFloors; floor++) {
        for (let unit = 1; unit <= unitPerFloor; unit++) {
          const index = (floor - 1) * unitPerFloor + (unit - 1);
          if (prevSubUnits[index]) {
            newSubUnits.push({
              ...prevSubUnits[index],
              floor: floor,
            });
          } else {
            newSubUnits.push({
              id: `subunit-${floor}-${unit}-${Date.now()}`,
              name: "",
              size: "",
              floor: floor,
              startDate: "",
              endDate: "",
              description: config.hasDescription ? "" : undefined,
            });
          }
        }
      }

      return newSubUnits;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noOfFloors, unitPerFloor, config.hasSubUnits, config.hasFloors]);

  useEffect(() => {
    if (!editData) return;

    const safeFormatDate = (dateValue: any): any => {
      if (!dateValue || dateValue === "N/A" || dateValue === "") {
        return null;
      }
      try {
        const parsed = dayjs(dateValue);
        if (parsed.isValid()) return parsed;
        return null;
      } catch (e) {
        console.warn("Invalid date format:", dateValue);
        return "";
      }
    };

    const formattedStartDate = safeFormatDate(editData.plannedStartDate);
    const formattedEndDate = safeFormatDate(editData.plannedEndDate);

    reset({
      unitName: editData.unitName || "",
      unitQty: editData.unitQty || "",
      noOfFloors: editData.noOfFloors || "",
      unitPerFloor: editData.unitPerFloor || "",
      plannedStartDate: formattedStartDate,
      plannedEndDate: formattedEndDate,
      captureSubUnits: editData.isChildExist ? "Yes" : "No",
      description: editData.description || "",
    });

    setPrevFloors(Number(editData.noOfFloors) || 0);
    setPrevUnitsPerFloor(Number(editData.unitPerFloor) || 0);

    const loadedSubUnits: SubUnit[] =
      (editData.subUnits || []).map((s: any) => ({
        id: s.key || s.id?.toString() || `subunit-api-${Date.now()}`,
        name: s.subUnitName || s.name || "",
        size: s.unit || s.size || "",
        floor: Number(s.floorNo || s.floor) || 1,
        startDate:
          s.plannedStartDate && s.plannedStartDate !== "N/A"
            ? dayjs(s.plannedStartDate).format("YYYY-MM-DD")
            : "",
        endDate:
          s.plannedEndDate && s.plannedEndDate !== "N/A"
            ? dayjs(s.plannedEndDate).format("YYYY-MM-DD")
            : "",
        description: s.description || "",
      })) || [];

    const floors = Number(editData.noOfFloors) || 0;
    const unitsPer = Number(editData.unitPerFloor) || 0;
    if (!config.hasFloors || !floors || !unitsPer) {
      setSubUnits(loadedSubUnits);
      return;
    }

    const grouped: Record<number, SubUnit[]> = {};
    loadedSubUnits.forEach((su) => {
      const f = Number(su.floor) || 1;
      if (!grouped[f]) grouped[f] = [];
      grouped[f].push(su);
    });

    const finalSubUnits: SubUnit[] = [];
    for (let floor = 1; floor <= floors; floor++) {
      const listForFloor = grouped[floor] || [];
      let assignedIdx = 0;

      for (let unit = 1; unit <= unitsPer; unit++) {
        if (assignedIdx < listForFloor.length) {
          const existing = listForFloor[assignedIdx];
          finalSubUnits.push({
            ...existing,
            floor,
          });
          assignedIdx++;
        } else {
          finalSubUnits.push({
            id: `subunit-${floor}-${unit}-${Date.now()}`,
            name: "",
            size: "",
            floor,
            startDate: "",
            endDate: "",
            description: config.hasDescription ? "" : undefined,
          });
        }
      }
    }

    setSubUnits(finalSubUnits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, config.hasDescription]);

  useEffect(() => {
    if (error?.message && onClearError) {
      const timer = setTimeout(() => {
        onClearError();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, onClearError]);

  const addSubUnit = () => {
    if (config.hasSubUnits) {
      const totalLimit = config.hasFloors ? noOfFloors * unitPerFloor : 9999;
      if (subUnits.length >= totalLimit) {
        setAlertInfo({
          type: "warning",
          message: `You can only add ${totalLimit} ${config.subUnitType}s.`,
        });
        return;
      }
      const currentFloor = Math.ceil((subUnits.length + 1) / unitPerFloor);

      const newSubUnit: SubUnit = {
        id: Date.now().toString(),
        name: "",
        size: "",
        floor: config.hasFloors ? currentFloor : 1,
        startDate: "",
        endDate: "",
      };

      if (config.hasDescription) {
        newSubUnit.description = "";
      }

      setSubUnits([...subUnits, newSubUnit]);
      setAlertInfo({ type: "", message: "" });
    }
  };

  const updateSubUnit = (id: string, field: string, value: any) => {
    setSubUnits((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
    if (field === "name" || field === "size") {
      setAlertInfo({ type: "", message: "" });
    }
  };

  const removeSubUnit = (id: string) => {
    setSubUnits((prev) => prev.filter((s) => s.id !== id));
  };

  const onSubmit = (data: any) => {
    setAlertInfo({ type: "", message: "" });

    if (data.plannedStartDate && data.plannedEndDate) {
      const start = dayjs(data.plannedStartDate);
      const end = dayjs(data.plannedEndDate);
      if (end.isBefore(start)) {
        setAlertInfo({
          type: "error",
          message:
            "Planned End Date cannot be earlier than Planned Start Date.",
        });
        return;
      }
    }

    if (config.hasSubUnits && captureSubUnits === "Yes") {
      const totalExpected = config.hasFloors
        ? noOfFloors * unitPerFloor
        : subUnits.length;

      if (subUnits.length === 0) {
        setAlertInfo({
          type: "warning",
          message: `Please add at least one ${config.subUnitType}.`,
        });
        return;
      }

      const incompleteRows = subUnits.filter(
        (s) =>
          !s.name?.trim() ||
          !s.size ||
          (config.hasFloors && (s.floor === undefined || s.floor === null))
      );

      if (incompleteRows.length > 0) {
        setAlertInfo({
          type: "warning",
          message: `${config.subUnitName}, ${config.subUnitSizeLabel}, and Floor are mandatory to fill for all ${config.subUnitType}s.`,
        });
        return;
      }

      if (config.hasFloors && subUnits.length < totalExpected) {
        setAlertInfo({
          type: "warning",
          message: `You must fill details for all ${totalExpected} ${config.subUnitType}s based on the # of Floors and ${config.subUnitType}s per Floor.`,
        });
        return;
      }
    }

    const payload: any = {
      unitName: data.unitName,
      unitQty: data.unitQty,
      noOfFloors: data.noOfFloors,
      unitPerFloor: data.unitPerFloor,
      plannedStartDate: data.plannedStartDate
        ? dayjs(data.plannedStartDate).format("YYYY-MM-DD")
        : "",
      plannedEndDate: data.plannedEndDate
        ? dayjs(data.plannedEndDate).format("YYYY-MM-DD")
        : "",
      description: data.description || "",
      unitType: config.unitLabel,
    };

    if (config.hasSubUnits) {
      payload.captureSubUnits = captureSubUnits;
      payload.isChildExist = captureSubUnits === "Yes";

      if (captureSubUnits === "Yes") {
        payload.subUnits = subUnits;
      } else {
        payload.subUnits = [];
      }
    }

    onSave(payload);
    reset();
    setSubUnits([]);
    setAlertInfo({ type: "", message: "" });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  const isMissing = (value: any) => {
    return !value || value.toString().trim() === "";
  };

  return (
    <div className="rounded mt-2" style={{ minWidth: "900px" }}>
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmit}>
          {error?.message && (
            <div className="mb-3">
              <Alert
                color={error.type === "error" ? "danger" : "warning"}
                className="d-flex align-items-center shadow-sm"
                style={{
                  fontSize: "0.9rem",
                  padding: "0.9rem 1rem",
                  borderRadius: "10px",
                  backgroundColor:
                    error.type === "error" ? "#fdecea" : "#fff4e5",
                  border: "1px solid",
                  borderColor: error.type === "error" ? "#f5c2c7" : "#ffeeba",
                }}
              >
                <Icon
                  name={
                    error.type === "error" ? "alert-circle" : "alert-triangle"
                  }
                  className="me-3"
                  style={{
                    fontSize: "1.25rem",
                    color: error.type === "error" ? "#b71c1c" : "#856404",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <strong className="d-block mb-1" style={{ fontWeight: 600 }}>
                    {error.type === "error" ? "Validation Error" : "Warning"}
                  </strong>
                  <span style={{ lineHeight: 1.5 }}>{error.message}</span>
                </div>
              </Alert>
            </div>
          )}

          <Row className="gy-3">
            <Col lg="6">
              <FormInput
                name="unitName"
                label={`${config.unitLabel} Name`}
                placeholder={`Enter ${config.unitLabel} Name`}
                required
                min={1}
                onInput={(e: any) => {
                  if (e.target.value < 0) e.target.value = e.target.value * -1;
                }}
              />
            </Col>

            {config.hasFloors && projectType !== "1" && projectType !== "5" && (
              <>
                <Col lg="6">
                  <FormInput
                    name="noOfFloors"
                    label="# of Floors"
                    type="number"
                    placeholder="Enter number of floors"
                    required
                    min={1}
                    onInput={(e: any) => {
                      if (e.target.value < 0)
                        e.target.value = e.target.value * -1;
                    }}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="unitPerFloor"
                    label={`# ${config.subUnitType}s per Floor`}
                    type="number"
                    placeholder={`Enter ${config.subUnitType}s per floor`}
                    required
                    min={1}
                    onInput={(e: any) => {
                      if (e.target.value < 0)
                        e.target.value = e.target.value * -1;
                    }}
                  />
                </Col>
              </>
            )}

            <Col lg="6">
              <FormInput
                name="unitQty"
                label={config.sizeLabel}
                type="number"
                placeholder={`Enter ${config.sizeLabel}`}
                required
                min={1}
                onInput={(e: any) => {
                  if (e.target.value < 0) e.target.value = e.target.value * -1;
                }}
              />
            </Col>

            <Col lg="6">
              <FormDatePicker
                name="plannedStartDate"
                label="Planned Start Date"
                placeholder="Select Start Date"
                required={false}
              />
            </Col>
            <Col lg="6">
              <FormDatePicker
                name="plannedEndDate"
                label="Planned End Date"
                placeholder="Select End Date"
                required={false}
              />
            </Col>

            {config.hasDescription && (
              <Col lg="12">
                <FormInput
                  name="description"
                  label="Description"
                  type="textarea"
                  placeholder="Enter description"
                />
              </Col>
            )}

            {config.hasSubUnits && (
              <Col lg="6">
                <Controller
                  name="captureSubUnits"
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label={`Capture ${config.subUnitType} ${config.subUnitType === "Milestone" ? "" : "Numbers"
                        }?`}
                      options={[
                        { label: "No", value: "No" },
                        { label: "Yes", value: "Yes" },
                      ]}
                      required
                    />
                  )}
                />
              </Col>
            )}
          </Row>

          {/* Subunit table */}
          {config.hasSubUnits && captureSubUnits === "Yes" && (
            <div className="mt-4">
              <h6 className="mb-3 fw-bold text-uppercase">
                {config.subUnitType} Details
              </h6>

              {projectType === "5" && captureSubUnits === "Yes" && (
                <div className="text-end mb-2">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={addSubUnit}
                    className="btn btn-primary"
                  >
                    + Add {config.subUnitType}
                  </Button>
                </div>
              )}

              <div style={{ overflowX: "auto" }}>
                <Table bordered hover responsive className="align-middle">
                  <thead className="table-light">
                    <tr>
                      {config.hasFloors && <th>Floor</th>}
                      {config.hasFloorType && <th>Floor Type</th>}
                      <th>{config.subUnitName}</th>
                      <th>{config.subUnitSizeLabel}</th>
                      {config.hasDescription && <th>Description</th>}
                      <th>Planned Start Date</th>
                      <th>Planned End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subUnits.map((s) => (
                      <tr key={s.id}>
                        {config.hasFloors && (
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              value={s.floor}
                              // readOnly
                              min={1}
                              onChange={(e) =>
                                updateSubUnit(
                                  s.id,
                                  "floor",
                                  Number(e.target.value)
                                )
                              }
                              onInput={(e: any) => {
                                if (e.target.value < 0)
                                  e.target.value = e.target.value * -1;
                              }}
                            />
                          </td>
                        )}
                        {config.hasFloorType && (
                          <td style={{ minWidth: "180px" }}>
                            <FormSelect
                              name={`subUnits.${s.id}.floorType`}
                              label=""
                              options={[
                                // { label: "Select Floor Type", value: "" },
                                ...(config.floorTypeOptions || []),
                              ]}
                              required={false}
                              onChange={(value: any) => {
                                updateSubUnit(s.id, "floorType", value);
                              }}
                            />
                          </td>
                        )}

                        <td>
                          <input
                            type="text"
                            // className="form-control"
                            className={`form-control ${isMissing(s.name) ? "subunit-error" : ""
                              }`}
                            value={s.name}
                            placeholder={`Enter ${config.subUnitType} Name`}
                            onChange={(e) =>
                              updateSubUnit(s.id, "name", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`form-control ${isMissing(s.size) ? "subunit-error" : ""
                              }`}
                            value={s.size}
                            placeholder={`Enter ${config.subUnitSizeLabel}`}
                            onChange={(e) =>
                              updateSubUnit(s.id, "size", e.target.value)
                            }
                            min={1}
                          // onInput={(e: any) => {
                          //   if (e.target.value < 0)
                          //     e.target.value = e.target.value * -1;
                          // }}
                          />
                        </td>
                        {config.hasDescription && (
                          <td>
                            <input
                              type="text"
                              className={`form-control ${isMissing(s.description) ? "subunit-error" : ""
                                }`}
                              value={s.description || ""}
                              placeholder="Enter description"
                              onChange={(e) =>
                                updateSubUnit(
                                  s.id,
                                  "description",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        )}
                        <td style={{ minWidth: "160px" }}>
                          <div className="subunit-datepicker">
                            <FormDatePicker
                              name={`subUnits.${s.id}.startDate`}
                              label=""          // Hides label
                              required={false}
                              placeholder="Select Start Date"
                              onChange={(value) =>
                                updateSubUnit(
                                  s.id,
                                  "startDate",
                                  value ? dayjs(value).format("YYYY-MM-DD") : ""
                                )
                              }
                              dateFormat="DD-MMM-YYYY"
                            />
                          </div>
                        </td>


                        {/* <input
                            type="date"
                            className="form-control"
                            value={s.endDate || "N/A"}
                            onChange={(e) =>
                              updateSubUnit(s.id, "endDate", e.target.value)
                            }
                          /> */}
                        <td style={{ minWidth: "160px" }}>
                          <div className="subunit-datepicker">
                            <FormDatePicker
                              name={`subUnits.${s.id}.endDate`}
                              label=""
                              required={false}
                              placeholder="Select End Date"
                              onChange={(value) =>
                                updateSubUnit(
                                  s.id,
                                  "endDate",
                                  value ? dayjs(value).format("YYYY-MM-DD") : ""
                                )
                              }
                              dateFormat="DD-MMM-YYYY"
                            />
                          </div>
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {alertInfo.message && (
                <div className="mt-3">
                  <Alert
                    color={alertInfo.type === "error" ? "danger" : "warning"}
                    className="alert-icon d-flex align-items-center"
                    style={{
                      fontSize: "0.9rem",
                      padding: "0.5rem 0.75rem",
                      borderRadius: "8px",
                    }}
                  >
                    <Icon
                      name={
                        alertInfo.type === "error"
                          ? "alert-circle"
                          : "alert-triangle"
                      }
                      className="me-2"
                    />
                    <div>{alertInfo.message}</div>
                  </Alert>
                </div>
              )}

              {/* {editData && (
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={addSubUnit}
                    type="button"
                    disabled={isAddDisabled}
                  >
                    + Add {config.subUnitType}
                  </Button>
                </div>
              )} */}
            </div>
          )}

          {/* Footer buttons */}
          <Row className="mt-4">
            <Col size="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    color="primary"
                    type="submit"
                  >
                    {editData ? "Update" : "Add"} {config.unitLabel}
                  </Button>
                </li>
                <li>
                  <Button
                    onClick={onCancel}
                    className="btn btn-secondary btn-md"
                    type="button"
                  >
                    <span>Cancel</span>
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddSiteStructureForm;
